import { useRef, useEffect, useMemo } from 'react';
import {
  Chart,
  registerables,
  ChartData,
  ChartOptions,
  Point,
  BubbleDataPoint,
} from 'chart.js';
import { months } from '../../const';
import { valueLabelsColumn } from '../../utils/chart-plugins/labels-column';
import ChartLegend from '../chart-legend/chart-legend';
import { DataValue } from '../../types/data';

Chart.register(...registerables);
Chart.defaults.font.family = 'Arial';
Chart.defaults.font.size = 14;
Chart.defaults.color = '#111928'

type ChartActionsProps = {
  data: DataValue[];
  openActions: DataValue[];
  closeActions: DataValue[];
  selectedYear: number;
};

function ChartActions({ data, openActions, closeActions, selectedYear }: ChartActionsProps) {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const monthsToShow = selectedYear < currentYear ? 12 : currentMonth + 1;

  const maxYAverageValue = Math.ceil(Math.max(...data.map(item => item.value)) / 20) * 20;

  const maxYActions = Math.ceil(
    Math.max(
      ...openActions.map(item => item.value),
      ...closeActions.map(item => item.value)
    ) / 20) * 20;

  const filteredOpenActions = openActions.slice(0, monthsToShow);
  const filteredCloseActions = closeActions.slice(0, monthsToShow);

  const filteredData = data
    .map(
      (item) =>
        item.value as
          | number
          | [number, number]
          | Point
          | BubbleDataPoint
          | null,
    );

  const chartData: ChartData = useMemo(
    () => ({
      labels: months.map((item) => item.substring(0, 3)),
      datasets: [
        {
          label: 'Открытые действия в шт',
          type: 'line',
          data: filteredOpenActions.map((item) => item.value),
          backgroundColor: '#01A9DB',
          borderColor: '#01A9DB',
          borderWidth: 4,
          yAxisID: 'y1',
        },
        {
          label: 'Закрытые действия в шт',
          type: 'line',
          data: filteredCloseActions.map((item) => item.value),
          backgroundColor: '#FBBF24',
          borderColor: '#FBBF24',
          borderWidth: 4,
          yAxisID: 'y1',
        },
        {
          label: 'Колонна в %',
          data: filteredData
            .filter((item) => item !== null)
            .map((item) => item),
          backgroundColor: '#8646F4',
          borderSkipped: 'bottom',
          borderRadius: 8,
          categoryPercentage: 0.65,
          yAxisID: 'y2',
        },
      ],
    }),
    [filteredOpenActions, filteredCloseActions, filteredData, monthsToShow, openActions, closeActions, data, selectedYear],
  );

  const chartOptions: ChartOptions = useMemo(
    () => ({
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false
        }
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: function(context) {
              const index = context.index;
              return index < monthsToShow ? 'rgba(0, 0, 0, 1)' : 'rgba(0, 0, 0, 0.3)';
            }
        }
        },
        y1: {
          beginAtZero: true,
          position: 'left',
          ticks: {
            callback: (value) => `${value} шт`,
            stepSize: 20,
            color: '#9CA3AF',
            font: {
              size: 12
            }
          },
          max: maxYActions,
        },
        y2: {
          beginAtZero: true,
          position: 'right',
          ticks: {
            callback: (value) => `${value} %`,
            stepSize: 20,
            color: '#9CA3AF',
            font: {
              size: 12
            }
          },
          max: maxYAverageValue,
        },
      },
    }),
    [monthsToShow, maxYActions, maxYAverageValue],
  );

  const legendLabels = chartData.datasets.map((dataset) => ({
    text: dataset.label as string,
    color: dataset.backgroundColor as string,
  }));

  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      chartInstanceRef.current = new Chart(ctx as CanvasRenderingContext2D, {
        type: 'bar',
        data: chartData,
        options: chartOptions,
        plugins: [valueLabelsColumn],
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, chartOptions]);

  return (
    <>
      <canvas ref={chartRef} />
      <ChartLegend labels={legendLabels} />
    </>
  );
}

export default ChartActions;
